import i18n from '@/plugins/i18n'
import cloneDeep from 'lodash/cloneDeep'
import router from '@/plugins/router'
import Vue from 'vue'
import {
  getErrorMessage,
  makeDefaultGetters,
  makeDefaultMutations,
  setState,
} from '@/utilities/store'
import { HTTP, SNACKBAR_DEFAULTS } from '@/utilities/constants'
import { config as env } from '@/plugins/env'

const { ERRORS, NON_ERRORS, NON_GLOBAL_ERRORS } = HTTP.STATUSES

const defaultState = () => ({
  apiEndpointsLoading: {},
  authType: null,
  layout: 'default-layout',
  errors: {},
  keepAlive: [],
  routerViewportOffsetTop: 0,
  snackbar: { ...SNACKBAR_DEFAULTS },
  tables: { itemsPerPage: 10 },
  uploadProgresses: [], // expects objects with url [string] and progress [integer] properties
  isLoading: false,
})

const properties = () => Object.keys(defaultState())

const defaultGetters = makeDefaultGetters(properties())

const defaultMutations = makeDefaultMutations(properties(), defaultState())

const state = defaultState()

const getters = {
  ...defaultGetters,
  apiEndpointIsLoading: state => opts => {
    const { method, url } = opts
    return !!state.apiEndpointsLoading[method]?.[url]
  },
  globalErrors: state => {
    const errors = cloneDeep(state.errors)
    return Object.keys(errors)
      .map(n => Number(n))
      .filter(s => ERRORS.filter(e => !NON_GLOBAL_ERRORS.includes(e)).includes(s))
      .map(e => String(e) + ': ' + (errors[e]?.message || i18n.t('common.error')))
  },
  uploadProgressByUrl: state => url => state.uploadProgresses.find(up => up.url === url)?.progress,
  validationError: state => error => state.errors[422]?.errors?.[error],
  validationErrors: state => state.errors[422]?.errors,
}

const actions = {
  closeSnackbar({ commit }) {
    commit('resetSnackbar')
  },
  forceLogout() {
    console.debug('force logout dispatched')
    // dispatch('redirect', '/login')
  },
  launchSnackbar({ commit }, params) {
    const sb = defaultState().snackbar
    // TODO: implement snackbar properly, either with vuetify or alternative, then remove this log
    console.debug('snackbar launched', params)
    commit('setSnackbar', { ...sb, ...params, visible: true })
  },
  onApiError({ commit, dispatch }, { data = {}, status } = {}) {
    if (!status || NON_ERRORS.includes(status)) return
    const { error, errors, message = getErrorMessage(status) } = data
    // if not authenticated, force logout
    if (status === 401 || status === 419) dispatch('forceLogout')
    commit('setErrors', { error, errors, message, status })
  },
  onInitialLoad({ commit }, { initialStateData }) {
    commit('loadInitialStateData', initialStateData)
  },
  onRouteChange({ commit }, { to, from }) {
    const l = to?.meta?.layout || 'default-layout'
    const old = from?.meta?.layout
    if (l !== old) commit('setLayout', l)
  },
  redirect({ commit }, url) {
    commit('resetErrors')
    router.push(url)
  },
}

const mutations = {
  ...defaultMutations,
  loadInitialStateData(state, data) {
    if (!data || typeof data !== 'object') return
    Object.keys(data).forEach(k => {
      Vue.set(state, k, cloneDeep(data[k]))
    })
  },
  resetState: state => setState(state, defaultState(), true),
  setApiEndpointsLoading(state, { method, url, value }) {
    const target = state.apiEndpointsLoading
    if (!target[method]) Vue.set(target, method, {})
    Vue.set(target[method], url, value)
  },
  setErrors(state, { status, message, errors }) {
    Vue.set(state.errors, status, { errors, message })
  },
  setIsLoading(state,isLoadingState){
    state.isLoading= isLoadingState
    
  },
  setUploadProgress(state, { url, progress }) {
    const target = state.uploadProgresses.find(v => v.url === url)
    if (target) target.progress = progress
    else state.uploadProgresses.push({ url, progress })
  },
}

export default {
  actions,
  getters,
  mutations,
  namespaced: true,
  state,
}
